/**
 * Factory para crear servicios de IA según el proveedor configurado.
 * Permite elegir proveedor desde la UI (localStorage) o usar el del servidor (.env).
 */

import OllamaService from './ollamaService';
import GroqService from './groqService';
import { log as debugLog } from '../utils/logger';
import { apiUrl, getApiBase } from '../utils/apiUrl';

const STORAGE_PROVIDER = 'preferredAIProvider'; // Solo qué proveedor mostrar en la UI; la clave Groq está en la BD

class AIServiceFactory {
  constructor() {
    this.provider = 'groq'; // Siempre usar Groq por defecto
    this.configLoaded = false;
    this.loggingEnabled = true;
    this.serviceInstance = null; // Singleton instance
  }

  /**
   * Carga la configuración desde el servidor y aplica preferencia de la UI (localStorage)
   * @returns {Promise<void>}
   */
  async loadConfig() {
    try {
      const response = await fetch(apiUrl('/api/config'), { credentials: 'include' });
      if (response.ok) {
        const config = await response.json();
        // Siempre usar Groq
        this.provider = 'groq';
        this.configLoaded = true;
        
        this.log('Configuración cargada desde servidor', {
          provider: this.provider,
          fromUI: !!uiProvider,
          config: config
        });
      } else {
        this.log('Error cargando configuración desde servidor', { status: response.status }, 'warn');
      }
    } catch (error) {
      this.log('Error cargando configuración desde servidor', { error: error.message }, 'error');
    }
  }

  /**
   * Log detallado de operaciones
   * @param {string} operation - Operación realizada
   * @param {Object} data - Datos relacionados
   * @param {string} level - Nivel de log (info, warn, error)
   */
  log(operation, data = {}, level = 'info') {
    if (!this.loggingEnabled) return;
    const timestamp = new Date().toISOString();
    debugLog(`[${timestamp}] AI_FACTORY ${level.toUpperCase()}: ${operation}`, data);
  }

  /**
   * Crea y retorna el servicio de IA apropiado (Singleton)
   * @returns {Promise<Object>} Instancia del servicio de IA
   */
  async createService() {
    // Cargar configuración si no está cargada
    if (!this.configLoaded) {
      await this.loadConfig();
    }

    // Retornar instancia existente si ya existe
    if (this.serviceInstance) {
      this.log('Reutilizando instancia existente del servicio de IA', { provider: this.provider });
      return this.serviceInstance;
    }

    this.log('Creando nueva instancia del servicio de IA', { provider: this.provider });

    // Configuración solo desde el servidor (BD por usuario); sin fuentes secundarias
    const response = await fetch(apiUrl('/api/config'), { credentials: 'include' });
    const config = response.ok ? await response.json() : {};

    // Siempre usar Groq
    this.log('Usando Groq como proveedor de IA');
    this.serviceInstance = new GroqService();
    // Pasar configuración al servicio
    this.serviceInstance.setConfig(config);

    return this.serviceInstance;
  }

  /**
   * Resetea la instancia del servicio (útil para cambios de configuración)
   */
  resetService() {
    this.log('Reseteando instancia del servicio de IA');
    this.serviceInstance = null;
    this.configLoaded = false;
  }

  /**
   * Guarda la preferencia de proveedor en la UI (localStorage) y resetea el servicio
   * @param {string} provider - 'openwebui' | 'groq'
   * @param {string} [groqApiKey] - API key de Groq (solo si provider === 'groq')
   */
  setPreferredProvider(provider, _groqApiKey = '') {
    if (typeof localStorage === 'undefined') return;
    // Siempre usar Groq
    localStorage.setItem(STORAGE_PROVIDER, 'groq');
    // La API key de Groq solo se guarda en la BD (PATCH /api/me/settings); no en localStorage
    this.resetService();
  }

  /**
   * Devuelve la preferencia de proveedor de la UI (solo cuál mostrar; la clave Groq viene de la BD).
   */
  getPreferredFromUI() {
    if (typeof localStorage === 'undefined') return { provider: null, groqApiKey: '' };
    return {
      provider: localStorage.getItem(STORAGE_PROVIDER),
      groqApiKey: '' // Siempre desde la BD por usuario
    };
  }

  /**
   * Obtiene información del proveedor actual
   * @returns {Promise<Object>} Información del proveedor
   */
  async getProviderInfo() {
    // Cargar configuración si no está cargada
    if (!this.configLoaded) {
      await this.loadConfig();
    }

    // Obtener configuración completa del servidor
    try {
      const response = await fetch(apiUrl('/api/config'), { credentials: 'include' });
      if (response.ok) {
        const config = await response.json();
        
        const providers = {
          openwebui: {
            name: 'IA local',
            description: 'Servidor de IA en tu máquina o red local',
            url: config.openWebUIUrl,
            model: config.aiModel,
            hasToken: !!config.openWebUIToken
          },
          groq: {
            name: 'IA en la nube',
            description: 'Recomendaciones personalizadas (API key)',
            url: 'https://api.groq.com',
            model: config.groqModel,
            hasToken: !!config.groqApiKey
          }
        };

        return providers[this.provider.toLowerCase()] || providers.openwebui;
      }
    } catch (error) {
      this.log('Error obteniendo información del proveedor', { error: error.message }, 'error');
    }

    // Fallback si no se puede cargar la configuración
    return {
      name: 'IA local',
      description: 'Servidor de IA en tu máquina o red local',
      url: 'http://localhost:11434',
      model: 'phi3:mini',
      hasToken: false
    };
  }

  /**
   * Lista todos los proveedores disponibles
   * @returns {Array} Lista de proveedores
   */
  getAvailableProviders() {
    return [
      {
        id: 'openwebui',
        name: 'IA local',
        description: 'Servidor de IA local',
        configured: !!(process.env.OPEN_WEBUI_URL && process.env.OPEN_WEBUI_TOKEN)
      },
      {
        id: 'groq',
        name: 'Groq',
        description: 'API de Groq con modelos Llama',
        configured: !!process.env.GROQ_API_KEY
      }
    ];
  }

  /**
   * Verifica si un proveedor está configurado
   * @param {string} providerId - ID del proveedor
   * @returns {boolean} True si está configurado
   */
  isProviderConfigured(providerId) {
    switch (providerId.toLowerCase()) {
      case 'openwebui':
        return !!(process.env.OPEN_WEBUI_URL && process.env.OPEN_WEBUI_TOKEN);
      case 'groq':
        return !!process.env.GROQ_API_KEY;
      default:
        return false;
    }
  }
}

// Crear instancia única del factory (Singleton)
let aiServiceFactoryInstance = null;

const getAIServiceFactory = () => {
  if (!aiServiceFactoryInstance) {
    aiServiceFactoryInstance = new AIServiceFactory();
  }
  return aiServiceFactoryInstance;
};

export default getAIServiceFactory();
